import axios from "axios";
import { last } from "lodash";
import { Dropdown, Nav, Navbar } from "react-bootstrap";
import ReactDOM from "react-dom/client";
import { Helmet } from "react-helmet";
import { QueryClient, QueryClientProvider } from "react-query";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AppRoutes } from "../components/routes";
import { useApiMutation } from "../utils/api";
import { initDevCorner } from "../utils/dev_corner";
import { truncateText } from "../utils/helpers";
import { ModalProvider } from "../utils/modal_launcher";

const csrfToken = (
  document.querySelector("meta[name=csrf-token]") as HTMLMetaElement
)?.content;
(window as any).csrfToken = csrfToken;
axios.defaults.headers.common["X-CSRF-Token"] = csrfToken;
axios.defaults.headers.common["Accept"] = "application/json";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});

if ((window as any).env.environment === "development") {
  initDevCorner();
}

const TopBarDropdown = () => {
  const { post } = useApiMutation();
  const userAttrs = (window as any).userAttrs;

  const groups: {
    apps: [string, () => void][];
    logout: [string, () => void, string][];
  } = {
    apps: [],
    logout: [],
  };

  if (userAttrs.revert) {
    groups.apps.push([
      "Switch to Admin",
      () =>
        post(`/api/common/become/revert`).then(
          () => (window.location.href = "/"),
        ),
    ]);
  }
  if (userAttrs.is_admin && !location.pathname.startsWith("/admin")) {
    groups.apps.push(["Switch to Admin", "/admin"]);
  }

  groups.logout.push([
    "Logout",
    () => post("/api/common/logout").then(() => (window.location.href = "/")),
    "red",
  ]);

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          variant=""
          className="btn-text"
          name="topbar-user-menu"
        >
          <span className="pr-1">
            {truncateText((window as any).userAttrs.display_name, 20)}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {Object.entries(groups).map(
            ([_, items], groupIndex, groupArray) =>
              items.length > 0 && (
                <>
                  {items.map(([name, path, className]) => (
                    <Dropdown.Item
                      key={name}
                      href={typeof path === "string" ? path : undefined}
                      onClick={typeof path === "function" ? path : undefined}
                      className={className || "text-color"}
                    >
                      {name}
                    </Dropdown.Item>
                  ))}
                  {groupIndex < groupArray.length - 1 && <Dropdown.Divider />}
                </>
              ),
          )}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

const inferTitle = (path, title) => {
  if (title) {
    return `AppleTracker - ${title}`;
  } else {
    return "AppleTracker";
  }
};

const App = () => {
  return (
    <>
      <ModalProvider>
        <Router>
          <Routes>
            {AppRoutes.map(([path, Component, title, children]: any) => (
              <Route
                key={path as string}
                path={path as string}
                element={
                  <>
                    <Helmet>
                      <title>{inferTitle(path, title)}</title>
                    </Helmet>
                    <Component />
                  </>
                }
              >
                {children?.map(([childPath, ChildComponent, childTitle]) => (
                  <Route
                    key={childPath}
                    path={childPath}
                    element={
                      <>
                        <Helmet>
                          <title>{inferTitle(childPath, childTitle)}</title>
                        </Helmet>
                        <ChildComponent />
                      </>
                    }
                  />
                ))}
              </Route>
            ))}
            <Route
              path="*"
              Component={() => {
                return <div>Not found</div>;
              }}
            />
          </Routes>
        </Router>
      </ModalProvider>

      <ToastContainer
        position="bottom-center"
        hideProgressBar
        autoClose={1750}
        transition={Slide}
      />
    </>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
);

const UserNavbar = () => (
  <Navbar bg="dark" expand="md" className="px-3">
    <Navbar.Brand href="/user" className="text-light">
      Apple Tracker 🍎
    </Navbar.Brand>
    <Navbar.Toggle aria-controls="navbar-content" />
    <Navbar.Collapse id="navbar-content">
      <Nav className="me-auto">
        <Nav.Link href="/user/dashboard" className="text-light">
          Dashboard
        </Nav.Link>
        <Nav.Link href="/user/logs" className="text-light">
          Logs
        </Nav.Link>
      </Nav>
      <TopBarDropdown />
    </Navbar.Collapse>
  </Navbar>
);

ReactDOM.createRoot(document.getElementById("navbar-root")!).render(
  <UserNavbar />,
);

window.addEventListener("keyup", (e) => {
  if (e.key === "e" && e.ctrlKey && (window as any).devToolStack.length) {
    const item = last(
      (window as any).devToolStack as { fn: () => void; args: any[] }[],
    );
    const { fn, args } = item;
    fn?.apply(this, args);
  }
});
